import React from "react";
import axios from "axios";
import {
    Alert,
    Box,
    CircularProgress,
    Container,
    Link,
    Paper,
    Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

function resolveUrl(pathOrUrl) {
    if (!pathOrUrl) return "";
    const value = String(pathOrUrl).trim();
    if (/^https?:\/\//i.test(value)) return value;
    return `${API_BASE}${value.startsWith("/") ? value : `/${value}`}`;
}

export default function PartnersPage() {
    const [partners, setPartners] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState("");

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError("");
            try {
                const res = await axios.get(`${API_BASE}/api/partners`, {
                    withCredentials: true,
                    params: { include_inactive: false },
                });
                if (!cancelled) setPartners(res.data || []);
            } catch (e) {
                if (!cancelled) setError(e?.response?.data?.error || e.message || "Partner konnten nicht geladen werden.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <Container sx={{ py: { xs: 4, md: 6 } }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
                    Unsere Partner
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: 17 }}>
                    Eine Auswahl unserer Partner und Kooperationen.
                </Typography>
            </Box>

            {loading && (
                <Box sx={{ py: 6, textAlign: "center" }}>
                    <CircularProgress />
                </Box>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            {!loading && !error && partners.length === 0 && (
                <Alert severity="info">Aktuell sind keine Partner hinterlegt.</Alert>
            )}

            {!loading && partners.length > 0 && (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" },
                        gap: 2,
                    }}
                >
                    {partners.map((partner) => {
                        const imageUrl = resolveUrl(partner.image_url);
                        const content = (
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2.5,
                                    minHeight: 190,
                                    height: "100%",
                                    borderRadius: 2,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1.5,
                                    textAlign: "center",
                                    transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
                                    "&:hover": partner.link_url ? {
                                        transform: "translateY(-2px)",
                                        boxShadow: "0 12px 28px rgba(15, 23, 42, 0.14)",
                                        borderColor: "primary.main",
                                    } : undefined,
                                }}
                            >
                                {imageUrl && (
                                    <Box
                                        component="img"
                                        src={imageUrl}
                                        alt={partner.title}
                                        loading="lazy"
                                        decoding="async"
                                        sx={{
                                            maxWidth: "100%",
                                            maxHeight: 96,
                                            objectFit: "contain",
                                        }}
                                    />
                                )}
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                    {partner.title}
                                </Typography>
                                {partner.link_url && (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "primary.main" }}>
                                        <Typography variant="body2">Website öffnen</Typography>
                                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                                    </Box>
                                )}
                            </Paper>
                        );

                        if (!partner.link_url) return <Box key={partner.id}>{content}</Box>;

                        return (
                            <Link
                                key={partner.id}
                                href={partner.link_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="none"
                                color="inherit"
                            >
                                {content}
                            </Link>
                        );
                    })}
                </Box>
            )}
        </Container>
    );
}
